<script setup lang="ts">
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {type Component, computed, h, inject, ref, useTemplateRef, watch} from "vue";
import {QMarkdown} from '@quasar/quasar-ui-qmarkdown'
import HelpMessageContentType from "@/utils/constants/HelpMessageContentType.ts";
import Translator from "@/utils/common/Translator.ts";
import {storeToRefs} from "pinia";
import {QTooltip} from "quasar";
import {OnClickOutside} from "@vueuse/components";
import {FieldToFocusSearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import {allFieldsKey, isPageChangedKey, widgetPageIndexKey} from "@/utils/presenters/interfaces.ts";
import DTOHelpMessage = namaTypes.DTOHelpMessage;
import DTOFieldHelpMessage = namaTypes.DTOFieldHelpMessage;

const props = defineProps<{
  entityType?: string,
  fieldId?: string
}>()

const emit = defineEmits<{
  (e: "onTooltipShow"): void;
}>()

const {fieldsHelpMessages, fieldToFocusOptions} = storeToRefs(useNamaFlagsStore())
const allFields = inject(allFieldsKey, ref([]));
const widgetPageIndex = inject(widgetPageIndexKey, 0)
const isPageChanged = inject(isPageChangedKey, ref(true))

const shouldShowTooltip = ref(false)
const entityType = props.entityType || ''

const helpMsgWrapperRef = useTemplateRef<HTMLDivElement>("helpMsgWrapperRef")
const qTooltipRef = useTemplateRef<QTooltip>("qTooltipRef")
const currentFieldHelpMsgs = computed<DTOFieldHelpMessage | undefined>(() => fieldsHelpMessages.value[entityType].find(fieldHelpMsg => fieldHelpMsg.fieldId == props.fieldId));
const fieldIdsWithHelpMsgs = computed(() => fieldsHelpMessages.value[entityType].map(help => help.fieldId));
const hasHelpMessages = computed(() => {
  if (ObjectChecker.isEmptyOrNull(fieldsHelpMessages.value[entityType]))
    return false;
  return ObjectChecker.isNotEmptyOrNull(currentFieldHelpMsgs.value)
});
const imActive = computed(() => Boolean(fieldToFocusOptions.value.field?.id == props.fieldId && isPageChanged.value))

function getCurrentFieldSearchItem(): FieldToFocusSearchItem | undefined {
  return allFields.value?.find(field => field.id == props.fieldId && field.record.extraData.pageIndex == widgetPageIndex)
}

const qTooltipModel = computed(() => shouldShowTooltip.value && isPageChanged.value)
const isFirst = computed(() => {
  const ids = fieldIdsWithHelpMsgs.value;
  if (!ids.length)
    return;
  const currentField = getCurrentFieldSearchItem();
  const allFieldsValue = allFields.value;
  if (!allFieldsValue)
    return;
  const index = allFieldsValue.findIndex(field => field == currentField)
  for (let i = index - 1; i >= 0; i--) {
    const field = allFieldsValue[i];
    if (ids?.includes(field.id))
      return false;
  }
  return true;
});

const isLast = computed(() => {
  const ids = fieldIdsWithHelpMsgs.value;
  if (!ids.length)
    return;
  const allFieldsValue = allFields.value;
  if (!allFieldsValue)
    return;
  const currentField = getCurrentFieldSearchItem();
  const index = allFieldsValue.findIndex(field => field == currentField)
  for (let i = index + 1; i < allFieldsValue.length; i++) {
    const field = allFieldsValue[i];
    if (ids.includes(field.id))
      return false;
  }
  return true;
})

function renderHelpMessage(helpMessage: DTOHelpMessage): Component | undefined {
  const message = Translator.calcByLanguage(helpMessage.arabic, helpMessage.english);
  if (!message)
    return;
  switch (helpMessage.contentType) {
    case HelpMessageContentType.HTML:
      return renderAsHTML(message);
    case HelpMessageContentType.Markdown:
      return renderAsMarkdown(message);
    default:
      return renderAsText(message)
  }
}

function renderAsHTML(message: string) {
  return h("div", {innerHTML: message})
}

function renderAsMarkdown(message: string) {
  const props = {
    src: message,
    noHtml: true
  };
  return h(QMarkdown, props)
}

function renderAsText(message: string) {
  return h("p", message)
}

async function switchPageAndExpandBlock(searchNext: boolean) {
  const allFieldsValue = allFields.value;
  if (!allFieldsValue)
    return;
  const currentField = getCurrentFieldSearchItem();
  const currentFieldIndex = allFieldsValue.findIndex(field => field == currentField)
  let fieldToStepInto: FieldToFocusSearchItem | undefined;

  const listOfFieldHelpMsgIds: (string | undefined)[] = fieldsHelpMessages.value[entityType].map(h => h.fieldId);
  if (!listOfFieldHelpMsgIds.length)
    return;
  const step = searchNext ? 1 : -1;
  let i = currentFieldIndex + step;
  const limit = searchNext ? allFieldsValue?.length : -1
  for (; i != limit; i += step) {
    const field = allFieldsValue[i];
    if (!field.id)
      continue
    if (listOfFieldHelpMsgIds.includes(field.id)) {
      fieldToStepInto = field;
      break;
    }
  }
  if (!fieldToStepInto)
    return;
  useNamaFlagsStore().setFieldToFocusOptions({field: fieldToStepInto, dontFocus: true})
}

async function stepToPrevious() {
  if (isFirst.value)
    return;
  await switchPageAndExpandBlock(false);
}

async function stepToNext() {
  if (isLast.value)
    return;
  await switchPageAndExpandBlock(true)
}

async function onHelpMsgClick() {
  const currentField = getCurrentFieldSearchItem();
  useNamaFlagsStore().setFieldToFocusOptions({field: currentField, dontFocus: true})
}

function onClickOutside() {
  shouldShowTooltip.value = false;
  fieldToFocusOptions.value.field = undefined
}

function isCurrentField(field: FieldToFocusSearchItem): boolean {
  return props.fieldId == field.id && widgetPageIndex == field.record.extraData.pageIndex;
}

function onTooltipShow() {
  qTooltipRef.value?.updatePosition();
}

watch(() => fieldToFocusOptions.value.field, async (field) => {
  if (!field)
    return;
  shouldShowTooltip.value = false;
  if (!isCurrentField(field))
    return;
  if (field.id?.includes("."))
    await new Promise(resolve => setTimeout(resolve, 200));
  shouldShowTooltip.value = true;
  helpMsgWrapperRef.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest'
  });
  emit("onTooltipShow")
}, {immediate: true, flush: "post"})
</script>

<template>
  <div ref="helpMsgWrapperRef" :class="{'active-primary':imActive}" class="tw-w-fit tw-relative" v-if="hasHelpMessages">
    <Transition name="slide-down">
      <span v-if="qTooltipModel"
            class="tw-absolute tw-border-b-namaBackground-deep-dark dark:tw-border-b-white tw-border-transparent tw-pointer-events-none tw-z-50 tw-top-[8px] -tw-translate-x-1/2"
            style="left:50%;border-width:10px"/>
    </Transition>
    <q-tooltip
      v-if="isPageChanged"
      @show="onTooltipShow" anchor="bottom middle"
      self="top middle"
      :offset="[10, 5]"
      no-parent-event
      :model-value="qTooltipModel"
      class="tw-px-4 tw-pt-2 tw-pb-6 dark:tw-text-black tw-text-gray-700 dark:tw-bg-white tw-bg-namaBackground-bright-dark tw-ring-2 tw-ring-black/10  tw-text-lg tw-rounded-xl tw-font-semibold tw-z-50 !tw-pointer-events-auto"
      ref="qTooltipRef"
    >
      <q-avatar square class="tw-size-5 tw-cursor-pointer tw-mb-3" @click="shouldShowTooltip=false">
        <q-icon name="close" class="dark:!tw-fill-black !tw-text-white dark:!tw-text-black"/>
      </q-avatar>
      <template v-for="obj in currentFieldHelpMsgs?.helps">
        <component :is="renderHelpMessage(obj)" class="tw-text-white dark:tw-text-black"/>
      </template>
      <div class="tw-space-x-2 tw-mt-2 tw-flex tw-items-center tw-flex-nowrap">
        <q-btn :label="Translator.translateId('previous')" v-if="!isFirst" @click.stop="stepToPrevious" class="tw-px-8 tw-rounded-lg"
               color="grey-8"
               text-color="white"
        />
        <q-btn :label="Translator.translateId('next')" v-if="!isLast" class="tw-px-8 tw-rounded-lg" color="primary" @click.stop="stepToNext"/>
      </div>
    </q-tooltip>
    <OnClickOutside :options="{ignore:[qTooltipRef],capture:false}" @trigger="onClickOutside">
      <NamaIcon default-icon="nm-show-help-msgs" class="tw-text-sm tw-size-5 tw-cursor-pointer" @click.stop="onHelpMsgClick"/>
    </OnClickOutside>
  </div>
</template>
